import { DragEvent, FC, useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import { useTranslation } from 'react-i18next';

interface Props {
    isShow: boolean;
    onHide: () => void;
    onSuccess: (created?: { name?: string; file?: File }) => void;
    initialName?: string;
}

const AddBank: FC<Props> = ({ isShow, onHide, onSuccess, initialName = '' }) => {
    const { t } = useTranslation('components.Modal.AddBank');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [name, setName] = useState<string>(initialName);
    const [file, setFile] = useState<File | null>(null);
    const [nameError, setNameError] = useState<string>('');
    const [fileError, setFileError] = useState<string>('');
    const [dragging, setDragging] = useState<boolean>(false);

    const selectFile = (nextFile?: File) => {
        if (!nextFile) return;
        if (!nextFile.name.toLowerCase().endsWith('.csv')) {
            setFile(null);
            setFileError(t('fileInvalid'));
            return;
        }
        setFile(nextFile);
        setFileError('');
    };

    const removeFile = () => {
        setFile(null);
        setFileError('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return `0 ${t('bytes')}`;
        const k = 1024;
        const sizes = [t('bytes'), 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setDragging(false);
        selectFile(event.dataTransfer.files?.[0]);
    };

    const handleSave = () => {
        const trimmedName = name.trim().replace(/\s+/g, ' ');
        const nextNameError = trimmedName ? '' : t('nameRequired');
        const nextFileError = file ? '' : t('fileRequired');
        setNameError(nextNameError);
        setFileError(nextFileError);
        if (nextNameError || nextFileError || !file) return;

        onSuccess({ name: trimmedName, file });
    };

    return (
        <Modal show={isShow} onHide={onHide} backdrop="static" animation={false} centered>
            <Modal.Header><Modal.Title>{t('title')}</Modal.Title></Modal.Header>
            <Modal.Body>
                <p className="text-muted">{t('description')}</p>
                <Form.Group controlId="formNewBankName" className="mb-3">
                    <Form.Label>{t('nameLabel')}<span className="text-danger">*</span></Form.Label>
                    <Form.Control type="text" autoFocus value={name}
                        onChange={(event: any) => { setName(event.target.value); setNameError(''); }}
                        placeholder={t('namePlaceholder')} />
                    {nameError && <small className="text-danger">{nameError}</small>}
                </Form.Group>
                <Form.Group controlId="formNewBankFile">
                    <Form.Label>{t('fileLabel')}<span className="text-danger">*</span></Form.Label>
                    <div className={`border rounded p-4 text-center ${dragging ? 'border-primary bg-light' : ''}`}
                        onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
                        onDragOver={(event) => event.preventDefault()} onDragLeave={() => setDragging(false)} onDrop={handleDrop}>
                        <input ref={fileInputRef} type="file" accept=".csv" className="visually-hidden"
                            onChange={(event) => selectFile(event.currentTarget.files?.[0])} />
                        {!file ? (
                            <>
                                <div>{t('dropFile')}</div>
                                <button type="button" className="btn btn-outline-primary btn-sm mt-2" onClick={() => fileInputRef.current?.click()}>
                                    {t('browseFiles')}
                                </button>
                            </>
                        ) : (
                            <div className="d-flex align-items-center justify-content-between p-2 bg-light rounded">
                                <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
                                    <i className="fa fa-file-csv-o text-primary me-2" style={{ fontSize: '24px' }}></i>
                                    <div className="text-start overflow-hidden">
                                        <div className="text-truncate" style={{ maxWidth: '300px' }}>{file.name}</div>
                                        <small className="text-muted">{formatFileSize(file.size)}</small>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-link text-danger p-1 ms-2"
                                    onClick={removeFile}
                                    aria-label={t('removeFile')}
                                    title={t('removeFile')}
                                >
                                    <i className="fa fa-times"></i>
                                </button>
                            </div>
                        )}
                    </div>
                    <small className="text-muted d-block mt-1">{t('acceptedFormat')}</small>
                    {fileError && <small className="text-danger d-block">{fileError}</small>}
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-cancel" onClick={onHide}>{t('cancel')}</button>
                <button type="button" className="btn btn-primary" onClick={handleSave}>{t('save')}</button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddBank;
